import React, { useState, useEffect } from 'react';
import { Zap, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { PaymentButton } from './PaymentButton';
import { paymentService } from '../services/paymentService';
import { productService } from '../services/productService';

export const BestSellers = () => {
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        setLoading(true);
        const data = await productService.getBestsellers();
        setBestSellers(data.products);
      } catch (err) {
        console.error('Failed to fetch bestsellers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBestSellers();
  }, []);

  const getProductTileClass = (productName) => {
    if (productName.includes('Berry')) return 'product-tile-berry';
    if (productName.includes('Citrus')) return 'product-tile-citrus';
    if (productName.includes('Green')) return 'product-tile-green';
    if (productName.includes('Tropical')) return 'product-tile-tropical';
    if (productName.includes('Midnight')) return 'product-tile-dark';
    if (productName.includes('Mixer') || productName.includes('Mocktail') || productName.includes('Mixed')) return 'product-tile-mixer';
    if (productName.includes('Tee') || productName.includes('Hoodie')) return 'product-tile-apparel';
    return 'bg-zinc-800';
  };

  if (loading) {
    return (
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto" />
        </div>
      </section>
    );
  }

  if (bestSellers.length === 0) {
    return null;  // Don't show section if no bestsellers
  }

  return (
    <section className="py-16 bg-black border-t border-zinc-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">
              Quick-Add <span className="text-amber-400">Best Sellers</span>
            </h2>
            <p className="text-gray-400">One-click add to cart. No browsing needed.</p>
          </div>
        </div>

        {/* Products Grid - Responsive for All Devices */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
          {bestSellers.map((product) => (
            <Card
              key={product.id}
              className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-all group relative overflow-hidden"
            >
              <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 items-end">
                <Badge className="bg-amber-400/90 text-black text-xs">
                  Bestseller
                </Badge>
                {product.ageRestricted && (
                  <Badge className="bg-red-600 text-white text-xs font-bold">
                    21+
                  </Badge>
                )}
              </div>
              
              <div className={`relative overflow-hidden aspect-square ${getProductTileClass(product.name)}`}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 product-image product-image-sharp"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              <CardContent className="p-3">
                <p className="text-xs text-gray-400 mb-1">{product.category} • {product.size}</p>
                <h3 className="text-sm font-bold text-white mb-2 line-clamp-2 group-hover:text-amber-400 transition-colors">
                  {product.name}
                </h3>
                <div className="mb-2">
                  <PaymentButton
                    packageId={paymentService.mapSkuToPackageId(product.id)}
                    productName={product.name}
                    price={product.price}
                    requiresAgeVerification={product.ageRestricted || false}
                    size="sm"
                    className="w-full bg-amber-400 hover:bg-amber-500 text-black font-bold text-xs"
                  >
                    ${product.price}
                  </PaymentButton>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom Banner */}
        <div className="mt-8 bg-gradient-to-r from-lime-400/10 to-amber-400/10 border border-lime-400/20 rounded-xl p-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Zap className="w-5 h-5 text-lime-400" />
            <p className="text-white font-bold">Join 50,000+ Energy Club Members</p>
          </div>
          <p className="text-gray-300 text-sm">
            Subscribe and get exclusive drops, early access, and up to 20% savings
          </p>
        </div>
      </div>
    </section>
  );
};
